import { Box, Group, Stack, Text } from '@mantine/core';
import { Camera, PersonStanding } from 'lucide-react';
import React from 'react';

export type BodyImageCameraOrbitIconProps = {
  /** Rotazione accumulata in gradi (negativo = sinistra, positivo = destra). */
  angle: number;
  /** Dimensione lato dell’icona in px. */
  size?: number;
  /** Mostra etichetta gradi sotto/accanto. */
  showLabel?: boolean;
  /** Layout etichetta. */
  labelPosition?: 'bottom' | 'right';
  className?: string;
};

/**
 * Vista compatta: omino Lucide al centro, orbit, marker telecamera.
 * A 0° la camera è di fronte (in basso).
 * Valori positivi spostano la camera in senso orario (dx), negativi in antiorario (sx).
 */
export const BodyImageCameraOrbitIcon: React.FC<
  BodyImageCameraOrbitIconProps
> = ({
  angle,
  size = 72,
  showLabel = true,
  labelPosition = 'bottom',
  className,
}) => {
  const normalized = ((angle % 360) + 360) % 360;
  const radians = (normalized * Math.PI) / 180;
  const orbitPercent = 34;
  // 0° = fronte (basso). Positivo = orario.
  const cameraLeft = 50 + orbitPercent * Math.sin(radians);
  const cameraTop = 50 + orbitPercent * Math.cos(radians);
  const personSize = Math.round(size * 0.42);
  const cameraMarkerSize = Math.round(size * 0.22);
  const cameraIconSize = Math.round(size * 0.13);

  const icon = (
    <Box
      className={className}
      role='img'
      aria-label={`Posizione telecamera: ${Math.round(angle)} gradi`}
      style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: '#F8F9FA',
        border: '1px solid #DEE2E6',
      }}
    >
      {/* Orbit ring */}
      <Box
        style={{
          position: 'absolute',
          inset: `${50 - orbitPercent}%`,
          borderRadius: '50%',
          border: '1.25px dashed #CED4DA',
          pointerEvents: 'none',
        }}
      />

      {/* Front marker (0°) */}
      <Box
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 3,
          width: 2,
          height: 6,
          marginLeft: -1,
          borderRadius: 1,
          backgroundColor: '#ADB5BD',
        }}
      />

      {/* Person */}
      <Box
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#495057',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PersonStanding size={personSize} strokeWidth={2} aria-hidden />
      </Box>

      {/* Camera */}
      <Box
        style={{
          position: 'absolute',
          left: `${cameraLeft}%`,
          top: `${cameraTop}%`,
          transform: `translate(-50%, -50%) rotate(${normalized}deg)`,
          width: cameraMarkerSize + 8,
          height: cameraMarkerSize + 8,
          borderRadius: '50%',
          backgroundColor: '#228BE6',
          border: '1.5px solid #fff',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
        }}
      >
        <Camera size={cameraIconSize} strokeWidth={2.25} aria-hidden />
      </Box>
    </Box>
  );

  if (!showLabel) {
    return icon;
  }

  const label = (
    <Text
      size='xs'
      c='dimmed'
      ta={labelPosition === 'bottom' ? 'center' : 'left'}
    >
      Camera {angle > 0 ? '+' : ''}
      {Math.round(angle)}°
    </Text>
  );

  if (labelPosition === 'right') {
    return (
      <Group gap='sm' align='center' wrap='nowrap'>
        {icon}
        <Stack gap={2}>
          <Text size='xs' fw={600}>
            Vista telecamera
          </Text>
          {label}
        </Stack>
      </Group>
    );
  }

  return (
    <Stack gap={4} align='center'>
      {icon}
      {label}
    </Stack>
  );
};
